import React, { Fragment, useContext, useState } from 'react'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import MessageContainer from 'presentation/shared/layouts/MessageContainer'
import CardNew from 'presentation/shared/components/CardNew'
import StatusTag from 'presentation/shared/components/StatusTag'
import { SurgicalOrderContext } from 'main/factories/pages/doctor/surgical-order/surgical-order-factory'
import { DocumentsList } from 'common/enum/documents-list'
import DocumentAccordion from 'presentation/shared/layouts/DocumentAccordeon'
import { useFormik } from 'formik'
import * as yup from 'yup'
import moment from 'moment-timezone'
import * as S from './styles'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory } from 'react-router'
import DocumentSuccessModal from './DocumentConfirmModal'
import { PendencyTypes } from 'presentation/shared/layouts/PendencyCard'
import { isSurgicalOrderDisabled } from 'common/utils/disableButtonByStatus'

type Props = {
  uploadDocument: (files: File[], type: string) => void
  deleteDocument: (document_id: number, group_id: string) => void
  downloadDocument: (
    document_id: number,
    group_id: string,
    type: keyof typeof DocumentsList
  ) => void
  answerSurgicalPendency: (values: { answer: string }) => void
  setDocumentSuccessModalIsOpen: (isOpen: boolean) => void
  documentSuccessModalIsOpen: boolean
  pendencyOrderId?: number
  onAcceptDivergence?: () => void
  onRequestReanalysis?: () => void
  onCancelSurgicalOrder?: () => void
  reloadPendencyHistory?: () => Promise<any>
}

const SurgicalOrderPendency = ({
  uploadDocument,
  deleteDocument,
  downloadDocument,
  answerSurgicalPendency,
  setDocumentSuccessModalIsOpen,
  documentSuccessModalIsOpen,
  pendencyOrderId,
  reloadPendencyHistory,
  onAcceptDivergence,
  onRequestReanalysis,
  onCancelSurgicalOrder
}: Props) => {
  const history = useHistory()
  const {
    state: { currentPendency }
  } = useContext<any>(SurgicalOrderContext)
  const [expandedDocument, setExpandedDocument] = useState('')
  const [showReplyForm, setShowReplyForm] = useState(false)
  const state = useContext<any>(SurgicalOrderContext).state

  const formik = useFormik({
    initialValues: {
      answer: ''
    },
    validationSchema: validationSchema,
    onSubmit: answerSurgicalPendency
  })

  const displayedHistoric = () => {
    if (!currentPendency?.historic) return []
    return [...currentPendency.historic].sort((a: any, b: any) => {
      const aPriority = a.is_timeline && a.profile === 'DOCTOR' ? 1 : 0
      const bPriority = b.is_timeline && b.profile === 'DOCTOR' ? 1 : 0
      if (aPriority !== bPriority) {
        return bPriority - aPriority
      }
      const dateA = moment(a.createdAt).valueOf()
      const dateB = moment(b.createdAt).valueOf()
      return dateA - dateB
    })
  }

  const AnswerPendency = () => {
    return (
      <ButtonNew
        size="large"
        disabled={
          !formik.isValid || !formik.values.answer || formik.isSubmitting
        }
        onClick={formik.submitForm}
        fullWidth
      >
        Enviar mensagem
      </ButtonNew>
    )
  }

  const isResolvedPendency = () => {
    return currentPendency.status !== 'RESOLVED'
  }

  const shouldShowReplyButton = () => {
    if (isDivergenceAuthPendency()) return false
    if (currentPendency?.type === PendencyTypes.DOCTOR_COMMUNICATION) {
      return (
        isResolvedPendency() &&
        (currentPendency.historic.length === 0 || showReplyForm)
      )
    }
    return isResolvedPendency()
  }

  const changeExpandedAccordeon = (
    accordeonLabel: string,
    isExpanded: boolean
  ) => {
    setExpandedDocument(isExpanded ? accordeonLabel : '')
  }

  const title =
    currentPendency?.type === PendencyTypes.HELP_CARD
      ? `Preciso de ajuda ${
          pendencyOrderId ? ('000' + pendencyOrderId).slice(-3) : ''
        }`
      : currentPendency?.type === PendencyTypes.DOCTOR_COMMUNICATION
      ? pendencyOrderId
        ? `Conversa ${('000' + pendencyOrderId).slice(-3)}`
        : 'Conversa Nº 001'
      : `Pendência ${
          pendencyOrderId ? ('000' + pendencyOrderId).slice(-3) : ''
        }`

  const subtitle =
    currentPendency?.type === PendencyTypes.DOCTOR_COMMUNICATION
      ? showReplyForm || currentPendency.historic.length === 0
        ? 'Escreva sua mensagem para o hospital'
        : undefined
      : undefined

  const handleSendAnotherMessage = async () => {
    if (reloadPendencyHistory) {
      const updated = await reloadPendencyHistory()
      if (updated?.status !== 'RESOLVED') {
        setShowReplyForm(true)
      }
    } else {
      setShowReplyForm(true)
    }
  }

  const SendAnotherMessageButton = () => {
    return (
      <ButtonNew size="large" onClick={handleSendAnotherMessage} fullWidth>
        Enviar outra mensagem
      </ButtonNew>
    )
  }

  const AUTH_DIVERGENCE_ACTION = 'CRMO_CREATED_PENDENCY_AUTH_DIVERGENCE'

  const isDivergenceAuthPendency = () => {
    const action =
      currentPendency?.action || currentPendency?.historic?.[0]?.action
    return action === AUTH_DIVERGENCE_ACTION
  }

  const hasDoctorAcceptedDivergence = () => {
    return currentPendency?.historic?.some(
      (h: any) => h.action === 'DOCTOR_ACCEPTED_DIVERGENCE'
    )
  }

  return (
    <>
      <ContainerNew
        title={title}
        subtitle={subtitle}
        gap="16px"
        primaryButton={shouldShowReplyButton() && <AnswerPendency />}
        subHeader={
          !showReplyForm && currentPendency.historic.length > 0 ? (
            <StatusTag status={currentPendency.status} type="pendency" />
          ) : undefined
        }
      >
        {!showReplyForm && currentPendency.historic.length > 0 && (
          <CardNew
            border="primaryLight100"
            shadow="none"
            padding="15px"
            style={{
              borderRadius: '20px'
            }}
          >
            {displayedHistoric().map(
              (
                { message, createdAt, responsible, profile, description }: any,
                index: number
              ) => {
                return (
                  <Fragment key={index}>
                    <MessageContainer
                      message={message}
                      createdAt={createdAt}
                      responsible={responsible}
                      profile={profile}
                      description={description}
                    />
                    {index !== displayedHistoric().length - 1 && (
                      <DividerNew
                        marginBottom="4px"
                        marginTop="4px"
                        leak="-8px"
                        color="neutral100"
                      />
                    )}
                  </Fragment>
                )
              }
            )}
          </CardNew>
        )}
        {currentPendency?.type === PendencyTypes.DOCTOR_COMMUNICATION &&
          isResolvedPendency() &&
          !isDivergenceAuthPendency() &&
          currentPendency.historic.length > 0 &&
          !showReplyForm && <SendAnotherMessageButton />}

        {currentPendency?.type !== PendencyTypes.HELP_CARD &&
          currentPendency?.type !== PendencyTypes.DOCTOR_COMMUNICATION &&
          !isDivergenceAuthPendency() && (
            <>
              <DividerNew marginBottom="0px" marginTop="0px" />
              {isResolvedPendency() && (
                <ButtonNew
                  size="large"
                  outlined
                  onClick={() =>
                    history.push('/pedido-cirurgico/documentos-de-pendencia')
                  }
                >
                  Incluir documento
                </ButtonNew>
              )}
            </>
          )}
        {currentPendency?.type !== PendencyTypes.DOCTOR_COMMUNICATION && (
          <S.FlexDiv>
            {currentPendency?.documents?.length > 0 && (
              <div>
                {currentPendency?.documents?.map(
                  (document: any, index: number) => {
                    return (
                      <Fragment key={index}>
                        <DocumentAccordion
                          key={document.key}
                          document={document}
                          padding="0"
                          hideHeaderDivider
                          handleChange={changeExpandedAccordeon}
                          expandedDocument={expandedDocument}
                          onDeleteFile={deleteDocument}
                          onDownloadFile={downloadDocument}
                          onUploadFile={
                            !['APPROVED', 'approved'].includes(document.status)
                              ? (files: any) =>
                                  uploadDocument(files, document.type)
                              : undefined
                          }
                          multiple
                          uploadButton={!isDivergenceAuthPendency()}
                        />
                        {currentPendency?.documents?.length - 1 !== index && (
                          <DividerNew marginBottom="8px" marginTop="8px" />
                        )}
                      </Fragment>
                    )
                  }
                )}
              </div>
            )}
            {isResolvedPendency() && (
              <DividerNew marginTop="0px" marginBottom="0px" />
            )}
          </S.FlexDiv>
        )}
        {isDivergenceAuthPendency() && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '16px'
              }}
            >
              {!hasDoctorAcceptedDivergence() && (
                <>
                  <ButtonNew
                    size="large"
                    fullWidth
                    onClick={onAcceptDivergence}
                    disabled={isSurgicalOrderDisabled(state.card.active_status)}
                  >
                    Aceitar divergência
                  </ButtonNew>
                  <ButtonNew
                    size="large"
                    outlined
                    fullWidth
                    onClick={onRequestReanalysis}
                    disabled={isSurgicalOrderDisabled(state.card.active_status)}
                  >
                    Solicitar reanálise
                  </ButtonNew>
                </>
              )}
              <ButtonNew
                size="large"
                variant="text"
                fullWidth
                onClick={onCancelSurgicalOrder}
                disabled={isSurgicalOrderDisabled(state.card.active_status)}
              >
                Cancelar pedido cirúrgico
              </ButtonNew>
            </div>
          </>
        )}

        {isResolvedPendency() && shouldShowReplyButton() && (
          <S.TextAreaContainer>
            <TextAreaNew
              label={
                currentPendency?.type === PendencyTypes.DOCTOR_COMMUNICATION
                  ? ''
                  : 'Adicionar comentário'
              }
              placeholder={
                currentPendency?.type === PendencyTypes.DOCTOR_COMMUNICATION
                  ? 'Escreva sua mensagem para o hospital'
                  : 'Adicionar um comentário para a CRMO'
              }
              value={formik.values.answer}
              onInputChange={formik.handleChange('answer')}
              rows={5}
            />
            <S.CharactersCounter
              size="xsmall"
              color={formik.isValid ? 'neutral400' : 'danger300'}
            >
              {formik.values.answer.length}/4000
            </S.CharactersCounter>
          </S.TextAreaContainer>
        )}
      </ContainerNew>

      <DocumentSuccessModal
        isOpen={documentSuccessModalIsOpen}
        onClose={() => setDocumentSuccessModalIsOpen(false)}
      />
    </>
  )
}

export default SurgicalOrderPendency

const validationSchema = yup.object().shape({
  answer: yup.string().required().max(4000, 'Limite de caracteres excedido')
})
